import { useState } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { UserRound } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

const avatarStyles = cva(
  'inline-flex items-center justify-center rounded-full select-none font-semibold shrink-0 overflow-hidden',
  {
    variants: {
      size: {
        xs: 'w-6 h-6 text-[10px]',
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

// Distinct palette — chosen for readability on white backgrounds
const AVATAR_COLORS = [
  'bg-red-500 text-white',
  'bg-orange-500 text-white',
  'bg-amber-500 text-white',
  'bg-lime-600 text-white',
  'bg-green-600 text-white',
  'bg-emerald-600 text-white',
  'bg-teal-600 text-white',
  'bg-cyan-600 text-white',
  'bg-sky-500 text-white',
  'bg-blue-600 text-white',
  'bg-indigo-500 text-white',
  'bg-violet-600 text-white',
  'bg-purple-600 text-white',
  'bg-fuchsia-600 text-white',
  'bg-pink-600 text-white',
  'bg-rose-600 text-white',
]

function nameHash(name: string): number {
  let h = 0
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0
  }
  return h
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  if (parts.length === 1 && parts[0].length > 0) {
    return parts[0][0].toUpperCase()
  }
  return ''
}

export interface EmployeeAvatarProps extends VariantProps<typeof avatarStyles> {
  name: string
  initials?: string
  avatarUrl?: string
  className?: string
}

export function EmployeeAvatar({ name, initials, avatarUrl, size, className }: EmployeeAvatarProps) {
  const [imgError, setImgError] = useState(false)

  const colorClass = AVATAR_COLORS[nameHash(name) % AVATAR_COLORS.length]
  const displayInitials = initials ?? deriveInitials(name)

  if (avatarUrl && !imgError) {
    return (
      <span className={cn(avatarStyles({ size }), 'bg-gray-100', className)}>
        <img
          src={avatarUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      </span>
    )
  }

  if (displayInitials) {
    return (
      <span
        className={cn(avatarStyles({ size }), colorClass, className)}
        aria-label={name}
        title={name}
      >
        {displayInitials}
      </span>
    )
  }

  return (
    <span
      className={cn(avatarStyles({ size }), 'bg-gray-200 text-gray-500', className)}
      aria-label={name}
      title={name}
    >
      <UserRound className="w-[55%] h-[55%]" />
    </span>
  )
}
